import { useQueries, type UseQueryResult } from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'

import { getDepartmentTransactions, queryKeys } from '@/api/fpa'
import type { Txn } from '@/api/types'

import type { DeptTxn } from '../lib/filterTxns'
import { usePeriodSelection } from './usePeriod'

export interface TransactionsState {
  rows: DeptTxn[]
  isLoading: boolean
  /** Every requested department failed (nothing to show). */
  isError: boolean
  /** Departments whose lines could not be loaded (the rest still render). */
  failedCodes: string[]
  /** Retry the failed departments. */
  retry: () => void
}

/**
 * Period-to-date transactions for one or more department tiles, tagged with their tile code.
 * Each department is its own cached query, so the overview and a department page share data.
 */
export function useTransactions(codes: readonly string[], enabled = true): TransactionsState {
  const { budgetId: _budgetId, ...period } = usePeriodSelection()
  const key = codes.join(',')
  const stableCodes = useMemo(() => (key ? key.split(',') : []), [key])

  const combine = useCallback(
    (results: UseQueryResult<Txn[]>[]): TransactionsState => {
      const failed = results.flatMap((result, index) => (result.isError ? [index] : []))
      return {
        rows: results.flatMap((result, index) =>
          (result.data ?? []).map((txn) => ({ ...txn, dept: stableCodes[index] ?? '' })),
        ),
        // Failed queries are settled: only queries still fetching their first data are loading.
        isLoading: results.some((result) => result.isPending && !result.isError),
        isError: results.length > 0 && failed.length === results.length,
        failedCodes: failed.map((index) => stableCodes[index] ?? ''),
        retry: () => {
          for (const index of failed) void results[index]?.refetch()
        },
      }
    },
    [stableCodes],
  )

  return useQueries({
    queries: stableCodes.map((code) => ({
      queryKey: queryKeys.transactions(code, period),
      queryFn: ({ signal }: { signal: AbortSignal }) => getDepartmentTransactions(code, period, signal),
      enabled,
    })),
    combine,
  })
}
